import { Colxx } from 'components/custom/CustomBootstrap';
import { UserContext } from 'contexts/user/UserContext';
import IntlMessages from 'helpers/IntlMessages';
import { getAuthorizeURI, isWechat, setLoginPayload } from 'helpers/helper';
import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import { Alert, Card, Row } from 'react-bootstrap';
import { isMobile } from 'react-device-detect';
import { NavLink, useLocation } from 'react-router-dom';
import LoginForm from './components/LoginForm';
import SocialLoginOptions from './components/SocialLoginOptions';

type RedirectTarget = string | { pathname?: string; search?: string; hash?: string };

const toRedirectPath = (target?: RedirectTarget): string | undefined => {
  if (!target) return undefined;
  if (typeof target === 'string') return target;
  return `${target.pathname ?? ''}${target.search ?? ''}${target.hash ?? ''}` || undefined;
};

const Login = ({ history }: any) => {
  const [loginState, setLoginState] = useState<{ alertId?: string; variant?: string; from?: RedirectTarget }>({});
  const { isAuthenticated, loading } = useContext(UserContext);
  const [show, setShow] = useState(false);
  const [redirectFrom, setRedirectFrom] = useState('/profile');
  const [isRedirectingToWechat, setIsRedirectingToWechat] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const location = useLocation();

  const isSafeRedirect = (url: string): boolean => {
    // Must be relative path (starts with /, not //)
    if (url.startsWith('/') && !url.startsWith('//')) return true;
    // Must be same origin absolute URL
    try {
      const redirectUrl = new URL(url, window.location.origin);
      return redirectUrl.origin === window.location.origin;
    } catch {
      return false;
    }
  };

  // Capture initial redirect URL and alert on mount
  useEffect(() => {
    const state = history.location?.state || {};
    setLoginState(state);
    if (state.alertId) {
      setShow(true);
    }
    if (state.from) {
      const normalized = toRedirectPath(state.from);
      if (normalized) setRedirectFrom(normalized);
    }
    setIsInitialized(true);
  }, [history]);

  // Auth redirect
  useEffect(() => {
    if (isAuthenticated) {
      const params = new URLSearchParams(location.search);
      const isSsoFlow = params.get('sso_flow') === 'true';

      // Normalize redirect target to string
      const rawRedirect = history.location?.state?.from;
      const safeRedirect = toRedirectPath(rawRedirect) || redirectFrom;

      if (isSsoFlow) {
        // If this login was initiated by Discourse SSO,
        // redirect back to the SSO endpoint to complete
        // the handshake using session data
        window.location.href = `/api/sso/discourse`;
      } else {
        // Standard web login. Redirect directly to destination
        window.location.href = isSafeRedirect(safeRedirect) ? safeRedirect : '/profile';
      }
    }
  }, [isAuthenticated, redirectFrom, location.search, history.location?.state]);

  // WeChat redirect
  useEffect(() => {
    if (isWechat && !isAuthenticated && !loading && isInitialized) {
      const params = new URLSearchParams(location.search);
      const isSsoFlow = params.get('sso_flow') === 'true';
      const redeemCode = params.get('redeemCode');
      let redirectURL = isSsoFlow ? `${location.pathname}${location.search}` : '/profile';
      if (!isSsoFlow && redeemCode) {
        redirectURL = `/profile?redeemCode=${encodeURIComponent(redeemCode)}`;
      } else if (!isSsoFlow) {
        const normalized = toRedirectPath(loginState.from);
        if (normalized && isSafeRedirect(normalized)) {
          redirectURL = normalized;
        }
      }
      setLoginPayload('social-login');
      setIsRedirectingToWechat(true);
      window.location.href = `${getAuthorizeURI('wechat', redirectURL)}`;
    }
  }, [isAuthenticated, loading, location.pathname, location.search, loginState.from, isInitialized]);

  if (loading || isRedirectingToWechat) {
    return <div className="loading" />;
  }

  // If the user is not authenticated, show the login form
  return (
    <div className="modal-dialog-centered mt-2 mb-2">
      <div className="container">
        <Row>
          <Colxx xxs="12" md="10" className="mx-auto my-auto">
            <Card className="auth-card">
              <div className="position-relative image-side d-none d-lg-block">
              </div>
              <div className="form-side">
                <div className="alert">
                  <Alert
                    variant={loginState.variant || 'danger'}
                    show={show}
                    onClose={() => setShow(false)}
                    dismissible
                  >
                    {loginState.alertId && <IntlMessages id={loginState.alertId} />}
                  </Alert>
                </div>
                <NavLink to="/" className="ml-5 mb-4 white d-flex justify-content-center">
                  <span className="logo-single" />
                </NavLink>
                <SocialLoginOptions isMobileBrowserChecker={isMobile} isWechatChecker={isWechat} />
                <h2 className="mt-5 mb-4">
                  <IntlMessages id="user.login-with-email" />
                </h2>
                <LoginForm />
              </div>
            </Card>
          </Colxx>
        </Row>
      </div>
    </div>
  );
};

Login.propTypes = {
  history: PropTypes.object,
};

export default Login;
